import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import WidgetHeader from './WidgetHeader';

interface AttentionCardProps { data: any | null; loading: boolean }

const AttentionCard: React.FC<AttentionCardProps> = ({ data, loading }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  if (loading) return (
    <div className={`rounded-lg border-l-4 border-rose-500 p-3 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <WidgetHeader compact icon={<FiAlertTriangle className="w-3.5 h-3.5 text-rose-400" />} title="Attention" accentColor="rose" />
      <div className={`h-8 animate-pulse rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
    </div>
  );

  const items = data?.items || data || [];
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div className={`rounded-lg border-l-4 border-rose-500 p-2 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        <WidgetHeader compact icon={<FiAlertTriangle className="w-3.5 h-3.5 text-rose-400" />} title="Attention" accentColor="rose" subtitle="All clear ✓" />
      </div>
    );
  }

  const bySeverity: Record<string, number> = { overdue: 0, blocked: 0, unassigned: 0 };
  items.forEach((item: any) => {
    if (item.type === 'overdue' || item.severity === 'red') bySeverity.overdue++;
    else if (item.type === 'blocked' || item.severity === 'orange') bySeverity.blocked++;
    else bySeverity.unassigned++;
  });

  return (
    <div className={`rounded-lg border-l-4 border-rose-500 p-3 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <WidgetHeader
        compact
        icon={<FiAlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
        title="Attention"
        accentColor="rose"
        actions={
          <button onClick={() => navigate('/work/chat')} className="p-1 text-[10px] text-rose-400 hover:text-rose-300 font-medium">
            Fix Now &rarr;
          </button>
        }
      />
      <div className="space-y-2">
        {bySeverity.overdue > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400 font-medium">{bySeverity.overdue} overdue</span>
          </div>
        )}
        {bySeverity.blocked > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-amber-400 font-medium">{bySeverity.blocked} blocked</span>
          </div>
        )}
        {bySeverity.unassigned > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-yellow-500" />
            <span className="text-yellow-400 font-medium">{bySeverity.unassigned} unassigned</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttentionCard;
